// --------------------------------------------------------------
// Created On: 2025-11-04
// Author: Marco Serrano
//
// Last Modified: 2025-12-02
// Modified By: Marco Serrano
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useMemo, useState, useRef } from "react";
import Spinner from "../../../../components/Spinner/Spinner";
import { CSVLink } from "react-csv";
import getLeadTime from "../../../../utilities/getLeadTime";
import CsvDownloadButton from "src/components/CsvDownloadButton/CsvDownloadButton";

// Responsible for exporting a BOM into a CSV
export default function ExportBom(props: Props) {
  const exportCsvButtonRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);
  const [leadTimeMap, setLeadTimeMap] = useState<{ [key: string]: string }>({});
  const [loadLeadTime, setLoadLeadTime] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [leadTimeLoadedCount, setLeadTimeLoadedCount] = useState<number>(0);
  const leadTimeAllLoaded = leadTimeLoadedCount === props.parts.length;
  const bomCsvData = useMemo(
    () => createBomCsvData(props.parts, props.companyDiscount),
    [props.parts, props.companyDiscount, leadTimeAllLoaded]
  );

  useEffect(() => {
    if (loadLeadTime) {
      props.parts.forEach((part) => {
        getLeadTime(
          part.partNumber,
          part.orderQuantity,
          part.isImpeller,
          part.paintColorId,
          null,
          null,
          (dateAvailable) => {
            const date = new Date(dateAvailable);
            const formattedDate = date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            });
            setLeadTimeMap((prev) => {
              return { ...prev, [part.partNumber]: formattedDate };
            });
            setLeadTimeLoadedCount((prev: number) => prev + 1);
          },
          () => {
            /* Do nothing. */
          },
          () => {
            /* Do nothing. */
          }
        );
      });
    }
  }, [loadLeadTime]);

  // Creates the CSV data with headers that can be used in quick order when we import parts from a CSV
  function createBomCsvData(parts: Part[], companyDiscount: number | null) {
    const csvData = [];
    const headers = [
      "part_number",
      "quantity",
      "impeller_trim_inches",
      "impeller_angle_degrees",
      "paint_color",
      "notes",
      "price",
      "discount_price",
      "weight",
      "ship_time",
    ];
    csvData.push(headers);

    // Only generate our data once all lead times have been collected
    if (!leadTimeAllLoaded) return csvData;

    parts.forEach((part) => {
      const dataRow = [
        part.partNumber,
        part.buildQuantity,
        "",
        "",
        part.isPaintable ? part.paintColorName : "",
        `"${part.description}"`,
        `$${part.price}` || "",
        companyDiscount ? `$${part.price - part.price * (companyDiscount / 100)}` : "",
        `${part.weight} lbs` || "",
        leadTimeMap[part.partNumber] || "",
      ];
      csvData.push(dataRow);
    });
    return csvData;
  }

  // Manually trigger a csv download once lead times have all loaded for the first time
  useEffect(() => {
    if (loadLeadTime && leadTimeAllLoaded) {
      setLoading(false);
      setLoadLeadTime(false);
      exportCsvButtonRef.current?.link.click();
    }
  }, [leadTimeAllLoaded, loadLeadTime]);

  return (
    <Fragment>
      <Spinner loading={loading} />
      <CsvDownloadButton
        ref={exportCsvButtonRef}
        onClick={() => {
          // Returning false here will cancel the download since we first need to load all lead times
          if (!leadTimeAllLoaded) {
            setLoadLeadTime(true);
            setLoading(true);
            return false;
          }
        }}
        className="text-white text-decoration-none btn btn-primary btn-md w-100 py-3 my-0 mb-2 mb-xl-0"
        data={bomCsvData}
        fileName={`${props.bomNumber}.csv`}
      >
        <div>
          <span className="d-none d-md-inline">Export CSV</span>
          <i className="d-inline d-md-none fa fa-2x fa-download" />
        </div>
      </CsvDownloadButton>
    </Fragment>
  );
}

interface Props {
  bomNumber: string;
  parts: Part[];
  companyDiscount: number | null;
}

interface Part {
  partNumber: string;
  description: string;
  buildQuantity: string;
  orderQuantity: string;
  paintColorId: number | null;
  paintColorName: string | null;
  isPaintable: boolean;
  isSellable: boolean;
  isImpeller: boolean;
  units: string;
  price: number;
  weight: number;
}
